import { SDKPageData } from "../_types";

export const JSON_SDK_MAJIK_UNIVERSAL_ID: SDKPageData = {
  id: "majik-universal-id",
  timestamp: new Date("2026-03-01").toISOString(),
  lastUpdate: new Date("2026-03-23").toISOString(),
  version: "1.0.0",
  isEnabled: true,
  metadata: {
    sdkName: "@majikah/majik-universal-id",
    sdkSlug: "majik-universal-id",
    tagline: "Post-quantum universal identity for the Majikah ecosystem",
    description:
      "A cryptographically anchored universal identity layer for the Majikah ecosystem. Binds a user, a MajikKey signing bundle, and a multi-stage KYC verification state into a single integrity-hashed record. Private personal information is always encrypted at rest with ML-KEM-768. Identity signing uses hybrid Ed25519 + ML-DSA-87. Supports a 5-stage Didit verification workflow that automatically graduates the identity tier from UNVERIFIED through to TRUSTED.",
    seo: "majik-universal-id post-quantum digital identity KYC verification ML-KEM-768 Ed25519 ML-DSA-87 Didit identity-verification SHA3-512 TypeScript JavaScript majikah",
    keywords: [
      "identity",
      "kyc",
      "post-quantum",
      "ml-kem-768",
      "ml-dsa-87",
      "ed25519",
      "didit",
      "verification",
      "universal-id",
      "cryptography",
      "majikah",
      "hybrid-crypto",
      "fips-203",
      "fips-204",
      "sha3-512",
      "tamper-proof",
      "private-info-encryption",
      "webhook",
    ],
    category: "Cryptography",
    subcategory: "Identity & KYC Verification",
    language: "TypeScript",
    platforms: [
      "Node.js",
      "Browser",
      "Electron",
      "Deno",
      "Bun",
      "Cloudflare Workers",
    ],
    photo: {
      logo: "https://storage.majikah.solutions/public/Logo_MajikMessage_Majikah_Thumbnail_WEBP.webp?url",
      banner:
        "https://storage.majikah.solutions/public/MajikMessage_SmallTile.jpg?url",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikMessage_SocialCard.webp?url",
    },
    badges: [
   {
        label: "Developed by",
        value: "Majikah Solutions OPC",
        type: "developed-by",
        url: "https://github.com/Majikah",
      },
      {
        label: "Version",
        value: "1.0.0",
        type: "version",
      },
      {
        label: "Downloads",
        value: "",
        type: "downloads",
      },
      {
        label: "Bundle Size",
        value: "",
        type: "bundle-size",
      },
      {
        label: "License",
        value: "Apache-2.0",
        type: "license",
        url: "https://opensource.org/licenses/Apache-2.0",
      },
      {
        label: "TypeScript",
        value: "Ready",
        type: "typescript",
      },
      {
        label: "Stars",
        value: "",
        type: "stars",
      },
      {
        label: "Language",
        value: "TypeScript",
        type: "language",
      },
      {
        label: "",
        value: "jedlsf",
        type: "sponsors",
      },
      {
        label: "Security",
        value: "Post-Quantum",
        type: "custom",
      },
    ],
  },
  repository: {
    url: "https://github.com/Majikah/majik-universal-id",
    owner: "Majikah",
    repo: "majik-universal-id",
    branch: "main",
    license: "Apache-2.0",
  },
  hero: {
    title: "Majik Universal ID",
    subtitle: "Post-Quantum Universal Identity",
    description:
      "A cryptographically anchored identity record that binds a user, a MajikKey signing bundle, and a 5-stage KYC verification state into a single integrity-hashed document. Private personal info is always encrypted at rest with ML-KEM-768. Content signing uses hybrid Ed25519 + ML-DSA-87 via Majik Signature. Identities can be created and managed at id.majikah.solutions.",
    codeSnippet: {
      language: "typescript",
      code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikUniversalID } from '@majikah/majik-universal-id';\n\n// Create a universal identity bound to a MajikKey\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\nconst majikId = await MajikUniversalID.create(user, key, {\n  account_id: 'acct_my_tenant',\n});\n\nconsole.log('ID:', majikId.id);\nconsole.log('Tier:', majikId.tier);   // IDTier.UNVERIFIED\nconsole.log('Hash:', majikId.hash);   // SHA3-512 integrity hash\n\n// Sign content as this identity\nconst signature = await majikId.signContent('My signed document', key);\n\n// Verify content\nconst result = majikId.verifyContent('My signed document', signature);\nconsole.log('Valid:', result.valid);  // true",
      caption:
        "Create a post-quantum anchored identity and sign content in a few lines.",
    },
    ctaButtons: [
      {
        label: "Install via npm",
        variant: "primary",
        url: "https://www.npmjs.com/package/@majikah/majik-universal-id",
        external: true,
      },
      {
        label: "Create Your ID",
        variant: "secondary",
        url: "https://id.majikah.solutions",
        external: true,
      },
      {
        label: "View on GitHub",
        variant: "tertiary",
        url: "https://github.com/Majikah/majik-universal-id",
        external: true,
      },
    ],
  },
  overview: {
    title: "What is Majik Universal ID?",
    paragraphs: [
      "Majik Universal ID is the identity layer of the Majikah ecosystem. It provides a single MajikUniversalID class that anchors a MajikUser account, a MajikKey cryptographic bundle, and a real-world KYC verification state into one integrity-hashed, tamper-evident record.",
      "Every MajikUniversalID is permanently bound 1:1:1 to exactly one user and one MajikKey. The binding is enforced by a SHA3-512 integrity hash that commits all four bound public keys (X25519, Ed25519, ML-KEM-768, ML-DSA-87) at creation time. Any modification to stored key material is detectable on deserialization.",
      "Private personal information — legal name, date of birth, contact details, address, government IDs — is always encrypted at rest using ML-KEM-768 via MajikEnvelope. It is never stored or serialized in plaintext. The decrypted data exists only in memory within the current session, and is stripped from all toJSON() and toBase64() outputs.",
      "Verification is performed through Didit, a third-party KYC provider, across up to five stages. Webhook payloads are processed by processWebhook(), which maps raw results to typed structures and automatically graduates the identity's IDTier from UNVERIFIED through BASIC, VERIFIED, ENHANCED, and up to TRUSTED. Verified identities are immutable — they cannot be mutated until revokeVerification() is called after a 30-day lock expires.",
      "Identities can be created and managed through the official web application at id.majikah.solutions using your own Majik Keys.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "Hybrid Post-Quantum Signing",
        description:
          "Ed25519 + ML-DSA-87 (FIPS-204) over core identity fields via Majik Signature. Both must verify — a break in one algorithm alone is not sufficient.",
      },
      {
        icon: "hash",
        label: "SHA3-512 Integrity Hash",
        description:
          "All four bound public keys are committed into a SHA3-512 hash at creation. Key material substitution or tampering is detectable on every deserialization.",
      },
      {
        icon: "lock",
        label: "ML-KEM-768 Private Encryption",
        description:
          "Private personal info is always encrypted at rest. The MajikEnvelope envelope is the only form ever persisted — plaintext data exists only in memory.",
      },
      {
        icon: "user",
        label: "5-Stage KYC Graduation",
        description:
          "IDTier is automatically derived from completed Didit stages: ID Verification → Liveness → Face Match → Phone → IP Analysis. All via webhook.",
      },
    ],
    whatItDoes: [
      "Anchors a MajikUser, a MajikKey bundle, and a KYC verification state into one integrity-hashed record",
      "Encrypts private personal info (PrivatePersonalInfo) at rest with ML-KEM-768 via MajikEnvelope",
      "Signs the core identity fields (id, user_id, timestamp, hash) with hybrid Ed25519 + ML-DSA-87 at creation",
      "Processes Didit V3 webhook payloads and automatically derives IDTier from passed stages",
      "Signs arbitrary content (bytes, strings, JSON) and files via Majik Signature",
      "Verifies content and embedded file signatures against the bound public keys",
      "Enforces immutability on verified identities — mutating operations throw MajikUniversalIDImmutableError",
      "Enforces a 30-day revocation lock after verification — revokeVerification() is blocked until the lock expires",
      "Shares encrypted private info with other recipients via MajikEnvelope group encryption",
      "Serializes to JSON and base64, with the rehydrated private info always stripped",
      "Provides a public-safe projection via toPublicView() for use in public APIs",
      "Manages consent records with grantConsent() and revokeConsent()",
    ],
    whatItDoesNot: [
      "Does not manage cryptographic keys — use @majikah/majik-key for that",
      "Does not make Didit API calls — session creation and token refresh live in a separate MajikDiditService package",
      "Does not rotate keys — a MajikUniversalID is permanently bound to one MajikKey; create a new identity to use a different key",
      "Does not verify real-world identity — it proves content was signed by a specific key, not who owns that key",
      "Does not store data — persistence is the application's responsibility",
      "Does not provide timestamping authority — timestamps are self-reported by the signer",
    ],
    targetUsers: [
      {
        title: "Majikah Platform Developers",
        description:
          "Integrate identity-bound KYC state into apps built on the Majikah ecosystem. Bind verified Majik Keys to real users and process Didit webhooks to graduate verification tiers.",
        icon: "code",
      },
      {
        title: "KYC & Compliance Engineers",
        description:
          "Build onboarding flows that anchor user identity to a cryptographic key, process webhook results across 5 Didit verification stages, and maintain a tamper-evident audit trail.",
        icon: "shield",
      },
      {
        title: "Self-Custody Identity App Builders",
        description:
          "Applications where users own their identity via a Majik Key. The identity record is portable, self-contained, and verifiable without a registry.",
        icon: "user",
      },
    ],
  },
  installation: {
    title: "Installation",
    packageManagers: [
      {
        name: "npm",
        command:
          "npm install @majikah/majik-universal-id @majikah/majik-key @majikah/majik-signature @majikah/majik-envelope",
      },
      {
        name: "yarn",
        command:
          "yarn add @majikah/majik-universal-id @majikah/majik-key @majikah/majik-signature @majikah/majik-envelope",
      },
      {
        name: "pnpm",
        command:
          "pnpm add @majikah/majik-universal-id @majikah/majik-key @majikah/majik-signature @majikah/majik-envelope",
      },
      {
        name: "bun",
        command:
          "bun add @majikah/majik-universal-id @majikah/majik-key @majikah/majik-signature @majikah/majik-envelope",
      },
    ],
    verification: [
      {
        description:
          "Verify the install by creating a minimal identity in Node.js:",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikUniversalID } from '@majikah/majik-universal-id';\n\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'test', 'Test Key');\nconsole.log('Key ready:', key.fingerprint);",
        expectedOutput: "Key ready: [base64-fingerprint]",
      },
    ],
  },
  quickStart: {
    title: "Quick Start",
    description:
      "The four most common workflows: creating an identity, signing and verifying content, loading and decrypting private info, and processing a Didit webhook.",
    steps: [
      {
        title: "1. Create a MajikUniversalID",
        description:
          "Pass a MajikUser and an unlocked MajikKey to MajikUniversalID.create(). The key must have all four public key fields present (Ed25519, ML-DSA-87, ML-KEM-768, X25519). Private personal info is encrypted immediately. The identity starts at IDTier.UNVERIFIED.",
        code: {
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikUniversalID } from '@majikah/majik-universal-id';\n\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\n\nconst majikId = await MajikUniversalID.create(user, key, {\n  account_id: 'acct_my_tenant',\n});\n\nconsole.log('ID:', majikId.id);             // UUIDv7\nconsole.log('Tier:', majikId.tier);         // IDTier.UNVERIFIED\nconsole.log('Hash:', majikId.hash);         // SHA3-512 integrity hash\nconsole.log('Mutable:', majikId.isMutable); // true\n\n// Private info is accessible immediately after create()\nconst info = majikId.privateInfo;\nconsole.log('Name:', info.legal_first_name, info.legal_last_name);\n\n// Serialize — private info (rehydrated) is always stripped\nconst json = majikId.toJSON();\nawait db.identities.insert({ id: majikId.id, data: json });",
        },
        notes: [
          "The key must be unlocked — call key.unlock(passphrase) if loading from storage.",
          "The key must have been imported via importFromMnemonicBackup() to have all four public key fields. Check key.isFullyUpgraded.",
          "toJSON() guarantees the rehydrated private info is stripped — it is never persisted.",
        ],
      },
      {
        title: "2. Sign and verify content",
        description:
          "Sign arbitrary content as this identity. The key fingerprint must match the bound signing_key.fingerprint. The identity must be at IDTier.UNVERIFIED (isMutable === true). Verification is read-only and has no tier requirement.",
        code: {
          language: "typescript",
          code: "// Sign content (requires isMutable === true)\nconst signature = await majikId.signContent('My signed statement.', key, {\n  contentType: 'text/plain',\n});\n\n// Verify — both Ed25519 and ML-DSA-87 must pass\nconst result = majikId.verifyContent('My signed statement.', signature);\nconsole.log('Valid:', result.valid);              // true\nconsole.log('Registered:', result.signer_registered); // true\nconsole.log('Signed at:', result.signed_at);\n\n// Tamper detection\nconst tampered = majikId.verifyContent('Modified statement.', signature);\nconsole.log('Tampered rejected:', tampered.valid); // false\n\n// Sign and embed into a file\nconst { blob: signedFile, handler } = await majikId.signFile(pdfBlob, key);\nconsole.log('File handler:', handler); // \"PDF\"\n\n// Verify embedded file signature\nconst fileResult = await majikId.verifyFile(signedFile);\nconsole.log('File valid:', fileResult.valid);",
        },
        notes: [
          "All signing methods throw MajikUniversalIDImmutableError if the identity is verified (tier !== UNVERIFIED).",
          "The key's fingerprint must match signing_key.fingerprint — MajikUniversalIDKeyNotFoundError is thrown otherwise.",
        ],
      },
      {
        title: "3. Load and decrypt private info",
        description:
          "Load a stored identity from JSON. Private info remains encrypted on load by default. Provide the key to fromJSON() for silent decryption, or call decryptPrivate() explicitly.",
        code: {
          language: "typescript",
          code: "// Option A: Silent decryption on load (swallows all failures)\nconst key = MajikKey.fromJSON(storedKey);\nawait key.unlock('passphrase');\n\nconst majikId = await MajikUniversalID.fromJSON(storedJson, { key });\n\nif (majikId.isPrivateDecrypted) {\n  const info = majikId.privateInfo;\n  console.log('DOB:', info.date_of_birth);\n}\n\n// Option B: Explicit decryption call (returns result, never throws)\nconst majikId2 = await MajikUniversalID.fromJSON(storedJson);\nconst result = await majikId2.decryptPrivate(key);\n\nif (result.success) {\n  console.log('Legal name:', result.data!.legal_first_name);\n} else {\n  console.warn('Decryption failed:', result.reason);\n}",
        },
        notes: [
          "fromJSON() validates structure and verifies the SHA3-512 integrity hash — MajikUniversalIDIntegrityError is thrown on hash mismatch.",
          "decryptPrivate() never throws — it returns { success, data, reason } so failures can be handled gracefully.",
          "Accessing privateInfo without prior decryption throws MajikUniversalIDPrivateInfoLockedError.",
        ],
      },
      {
        title: "4. Process a Didit webhook",
        description:
          "Process an incoming Didit V3 webhook payload to graduate the identity's tier. The processWebhook() method verifies the HMAC, maps stages, derives the new IDTier, and returns user sync actions for the caller to apply.",
        code: {
          language: "typescript",
          code: "// In your webhook handler\nconst result = await majikId.processWebhook(payload, headers, webhookSecret);\n\nconsole.log('Previous tier:', result.previous_tier);\nconsole.log('New tier:', result.new_tier);\nconsole.log('Tier changed:', result.tier_changed);\nconsole.log('All stages passed:', result.all_stages_passed);\nconsole.log('User sync actions:', result.user_sync_actions);\n// e.g. ['verifyPhone', 'verifyIdentity']\n\n// Apply sync actions to the linked MajikUser\nfor (const action of result.user_sync_actions) {\n  if (action === 'verifyIdentity') await user.setIdentityVerified(true);\n  if (action === 'verifyPhone')    await user.setPhoneVerified(true);\n  if (action === 'restrict')       await user.restrict();\n}\n\n// Persist the updated identity\nawait db.identities.update({ id: majikId.id, data: majikId.toJSON() });",
        },
        notes: [
          "payload.vendor_data must equal MajikUniversalID.id. Set this when creating the Didit session.",
          "processWebhook() does NOT require isMutable — it is the graduation path and runs regardless of current tier.",
          "Webhook HMAC verification uses verifyWebhookSignatureRaw() internally against x-signature-v2, x-signature-simple, and x-signature headers.",
        ],
      },
    ],
  },
  features: [
    {
      id: "integrity-hash",
      title: "SHA3-512 Integrity Hash",
      description:
        "A SHA3-512 hash is computed at creation over the identity's canonical fields — binding all four public keys from the bound MajikKey. Any modification to stored key material is detectable on deserialization.",
      icon: "hash",
      content: [
        "The canonical input is: id : user_id : timestamp : x25519_pk : ed_pk : ml_kem_pk : ml_dsa_pk (colon-delimited).",
        "All four public keys are committed — X25519, Ed25519, ML-KEM-768, and ML-DSA-87. Substituting even one key produces a different hash.",
        "fromJSON() recomputes and verifies the hash on every load. A mismatch throws MajikUniversalIDIntegrityError before the instance is constructed.",
        "The validate() method also re-verifies the hash so application code can check integrity at any point.",
        "The hash is the primary tamper-detection mechanism for the identity record itself — separate from the content signatures produced by Majik Signature.",
      ],
      codeExample: {
        title: "Verify integrity manually",
        language: "typescript",
        code: "const majikId = await MajikUniversalID.fromJSON(storedJson);\n// fromJSON() verifies the SHA3-512 hash automatically\n// If the hash mismatches, MajikUniversalIDIntegrityError is thrown\n\n// You can also validate at any time\nconst validation = majikId.validate();\nif (!validation.is_valid) {\n  console.error('Integrity errors:', validation.errors);\n}\nconsole.log('Warnings:', validation.warnings);",
      },
      benefits: [
        "Detects any offline modification to the stored JSON before use",
        "All four cryptographic public keys are committed — key material substitution is immediately caught",
        "Integrity check is automatic on every fromJSON() / fromBase64() call — no manual step required",
      ],
    },
    {
      id: "hybrid-identity-signing",
      title: "Hybrid Post-Quantum Identity Signing",
      description:
        "The core identity fields are signed at creation using hybrid Ed25519 + ML-DSA-87 via Majik Signature. Both algorithms must verify for the identity record to be considered authentic.",
      icon: "shield",
      content: [
        "The signed canonical payload is JSON.stringify({ id, user_id, timestamp, hash }) with contentType: 'application/majikid'.",
        "The signature is produced by MajikSignature.sign() using the same dual-algorithm architecture as Majik Signature — Ed25519 (128-bit classical) and ML-DSA-87 (FIPS-204, Category 5 post-quantum).",
        "The full MajikSignature envelope (signerEdPublicKey, signerMlDsaPublicKey, edSignature, mlDsaSignature, contentHash, timestamp) is stored in metadata.signature as a MajikIDSignature.",
        "A break in either algorithm alone is not sufficient to forge the identity binding.",
        "The same signing infrastructure is also used for signContent() and signFile() when users sign arbitrary content as their identity.",
      ],
      table: {
        headers: [
          "Algorithm",
          "Type",
          "Security Level",
          "Signature Size",
          "Public Key Size",
        ],
        rows: [
          {
            cells: [
              "Ed25519",
              "Classical (ECC)",
              "128-bit",
              "64 bytes",
              "32 bytes",
            ],
          },
          {
            cells: [
              "ML-DSA-87",
              "Post-Quantum (Lattice)",
              "NIST Category 5",
              "4595 bytes",
              "2592 bytes",
            ],
          },
        ],
      },
      benefits: [
        "Hybrid downgrade resistance — both classical and post-quantum must be broken simultaneously",
        "Future-proof against quantum computing advances",
        "Reuses the proven Majik Signature infrastructure — no bespoke signature scheme",
      ],
    },
    {
      id: "private-info-encryption",
      title: "ML-KEM-768 Private Info Encryption",
      description:
        "Private personal information is always encrypted at rest using the bound key's ML-KEM-768 public key via MajikEnvelope. It is never stored or serialized in plaintext — the decrypted value exists only in memory within the current session.",
      icon: "lock",
      content: [
        "PrivatePersonalInfo (legal name, DOB, nationality, gender, contact details, address, government IDs) is encrypted immediately in create().",
        "Encryption uses MajikEnvelope.encrypt() with a single recipient — the bound MajikKey's ML-KEM-768 public key. Only the holder of the corresponding ML-KEM-768 secret key can decrypt.",
        "The envelope (EncryptedPrivateInfo) is the only form ever written to JSON or base64. toJSON() and toBase64() always strip the rehydrated field via _serializeMetadata().",
        "fromJSON() with a { key } option attempts silent decryption on load — all failures are swallowed. Call decryptPrivate(key) explicitly to handle failures.",
        "decryptPrivate(key) returns { success, data, reason } — it never throws. Failures are returned, not raised.",
        "sharePrivate({ senderKey, recipients }) re-encrypts for multiple recipients using MajikEnvelope group encryption and returns a scanner string.",
        "_validateJSON() rejects any stored payload where metadata.private is not in EncryptedPrivateInfo format — plaintext private info is never accepted on load.",
      ],
      codeExample: {
        title: "Decrypt and share private info",
        language: "typescript",
        code: "// Decrypt explicitly\nconst result = await majikId.decryptPrivate(unlockedKey);\nif (result.success) {\n  console.log('Phone:', result.data!.primary_phone);  // E.164 format\n  console.log('Tax ID:', result.data!.tax_id);        // SENSITIVE\n}\n\n// Share with another recipient (does not mutate the instance)\nconst scannerString = await majikId.sharePrivate({\n  senderKey: unlockedKey,\n  recipients: [recipientKey], // only mlKemPublicKey needed — no unlock required\n});\n\n// Recipient decrypts with their own key\nconst env = MajikEnvelope.fromScannerString(scannerString);\nconst json = await env.decrypt({\n  fingerprint: recipientKey.fingerprint,\n  mlKemSecretKey: recipientKey.mlKemSecretKey,\n});\nconst privateInfo: PrivatePersonalInfo = JSON.parse(json);",
      },
    },
    {
      id: "immutability-locking",
      title: "Immutability & 30-Day Revocation Lock",
      description:
        "Once a MajikUniversalID reaches any verified tier (above UNVERIFIED), it becomes immutable. All mutating operations throw MajikUniversalIDImmutableError. Revocation itself is blocked for 30 days after verification to prevent abuse.",
      icon: "shield",
      content: [
        "isMutable returns true only when tier === IDTier.UNVERIFIED. All mutating methods (signContent, signFile, syncUserRef, updateDisplayName, updateSettings, grantConsent, revokeConsent) call _assertMutable() at entry.",
        "processWebhook() and applyDiditResult() are NOT subject to the mutable check — they are the graduation path and must run regardless of current tier.",
        "revokeVerification(reason) resets the identity to IDTier.UNVERIFIED but throws MajikUniversalIDVerificationLockedError if called within 30 days of the last verifiedAt timestamp.",
        "The 30-day window is checked by isVerificationLocked() and verificationLockDaysRemaining — both derived from verifiedAt.",
        "requireReverification(reason) flags the identity for re-verification (admin use) without resetting the tier or unlocking the identity.",
      ],
      codeExample: {
        title: "Immutability and revocation lock",
        language: "typescript",
        code: "import {\n  MajikUniversalID,\n  MajikUniversalIDImmutableError,\n  MajikUniversalIDVerificationLockedError,\n} from '@majikah/majik-universal-id';\n\n// Mutating a verified identity throws immediately\ntry {\n  await majikId.signContent('test', key);\n} catch (e) {\n  if (e instanceof MajikUniversalIDImmutableError) {\n    console.log('Cannot sign — identity is verified:', e.current_tier);\n  }\n}\n\n// Check the revocation lock before attempting\nif (majikId.isVerificationLocked) {\n  console.log('Lock days remaining:', majikId.verificationLockDaysRemaining);\n} else {\n  majikId.revokeVerification('User requested reset');\n  console.log('Tier after revocation:', majikId.tier); // IDTier.UNVERIFIED\n}",
      },
      benefits: [
        "Verified identities cannot be silently mutated — every attempt surfaces as a typed error",
        "The 30-day lock prevents rapid verification → revocation → re-verification cycles",
        "processWebhook() bypasses the mutability gate, keeping the graduation path always open",
      ],
    },
    {
      id: "didit-webhook-processing",
      title: "Didit Webhook Processing",
      description:
        "processWebhook() accepts a raw Didit V3 webhook payload, verifies the HMAC signature, maps each stage to typed MajikID structures, derives the new IDTier, and returns user sync actions for the caller to apply on the linked MajikUser.",
      icon: "refresh",
      content: [
        "Webhook HMAC is verified via verifyWebhookSignatureRaw() — tries x-signature-v2 (canonical sorted+rounded JSON), x-signature-simple (session fields), and x-signature (raw body) in order.",
        "vendor_data in the payload must equal MajikUniversalID.id — a mismatch throws MajikUniversalIDWebhookPayloadError before any state is modified.",
        "5 Didit stages are mapped to typed structures: ID Verification (DiditIDVerification), Liveness (DiditLiveness), Face Match (DiditFaceMatch), Phone Verification (DiditPhoneVerification), IP Analysis (DiditIPAnalysis).",
        "AML screening is mapped as a supplemental check (DiditAMLScreening) — PEP and sanctions flags are tracked.",
        "IDTier is derived from the set of passed stages: PHONE only → BASIC, ID only → VERIFIED, ID+Liveness+FaceMatch → ENHANCED, all 5 → TRUSTED.",
        "user_sync_actions is a list of string actions (verifyPhone, verifyIdentity, unverifyIdentity, restrict) for the caller to apply to the linked MajikUser.",
        "Session statuses 'Abandoned', 'Expired', and 'Kyc Expired' are treated as terminal alongside 'Approved' and 'Declined'.",
        "The underlying DiditWebhookMapperImpl is exported for advanced use — custom webhook handlers can call stage mappers (mapIDVerification, mapLiveness, etc.) individually.",
      ],
      table: {
        headers: ["Stage", "DiditStage Enum", "Tier Contribution"],
        rows: [
          {
            cells: [
              "1 — ID Verification",
              "DiditStage.ID_VERIFICATION",
              "VERIFIED (Stage 1 alone)",
            ],
          },
          {
            cells: [
              "2 — Liveness",
              "DiditStage.LIVENESS",
              "Required for ENHANCED",
            ],
          },
          {
            cells: [
              "3 — Face Match",
              "DiditStage.FACE_MATCH",
              "Required for ENHANCED",
            ],
          },
          {
            cells: [
              "4 — Phone Verification",
              "DiditStage.PHONE_VERIFICATION",
              "BASIC (phone alone), Required for TRUSTED",
            ],
          },
          {
            cells: [
              "5 — IP Analysis",
              "DiditStage.IP_ANALYSIS",
              "Required for TRUSTED",
            ],
          },
        ],
      },
    },
    {
      id: "content-signing-verification",
      title: "Content Signing & Verification",
      description:
        "Sign arbitrary content or files as a MajikUniversalID identity. Verification checks the signer fingerprint against the bound key before performing cryptographic verification via Majik Signature.",
      icon: "file",
      content: [
        "signContent(content, key, options?) — signs bytes or strings with hybrid Ed25519 + ML-DSA-87. Delegates to MajikSignature.sign().",
        "signFile(file, key, options?) — signs a file and embeds the signature into its native metadata. Delegates to MajikSignature.signFile(). Supports all file formats from Majik Signature (PDF, PNG, WAV, MP4, DOCX, etc.).",
        "verifyContent(content, signature, context?) — verifies a MajikSignature against the bound public keys. Checks signer_fingerprint === signing_key.fingerprint before cryptographic verification. Returns ContentVerificationResult.",
        "verifyFile(file, context?) — extracts the embedded signature, checks the fingerprint, then verifies via MajikSignature.verifyFile(). Returns FileVerificationResult with an optional handler field.",
        "verifyText(text, signature, context?) — convenience wrapper for verifyContent() for string content.",
        "Both signContent() and signFile() require isMutable === true and will throw MajikUniversalIDImmutableError on verified identities.",
      ],
      codeExample: {
        title: "Sign and verify a file as an identity",
        language: "typescript",
        code: "// Sign a PDF as this identity\nconst { blob: signedPdf, handler } = await majikId.signFile(pdfBlob, key, {\n  contentType: 'application/pdf',\n});\nconsole.log('Signed using handler:', handler); // \"PDF\"\n\n// Verify the file signature later (no tier requirement)\nconst result = await majikId.verifyFile(signedPdf);\nif (result.valid) {\n  console.log('Authentic. Signer registered:', result.signer_registered);\n  console.log('Content hash:', result.content_hash);\n  console.log('Handler:', result.handler);\n} else {\n  console.log('Invalid:', result.reason);\n}",
      },
    },
    {
      id: "serialization-projection",
      title: "Serialization & Public View",
      description:
        "MajikUniversalID serializes to JSON or base64. The rehydrated private info is always stripped from all serialization outputs. toPublicView() provides a safe projection for public-facing APIs.",
      icon: "file",
      content: [
        "toJSON() returns MajikUniversalIDJSON — a plain object with all fields except the rehydrated private info. Safe to store in any database.",
        "toBase64() encodes toJSON() as a base64 string for compact storage or transport.",
        "fromJSON(json, options?) validates structure, verifies the SHA3-512 hash, and optionally attempts silent decryption if { key } is provided.",
        "fromBase64(b64, options?) decodes and delegates to fromJSON().",
        "toPublicView() returns MajikIDPublicView — a projection with no private info, no signature records. Includes the public profile, tier, status, display name, public signing key material, and a verification_stages map (Record<DiditStage, boolean>) where all five stage keys are always present.",
        "validate() returns { is_valid, errors, warnings } — structural and integrity checks including re-verification of the SHA3-512 hash.",
      ],
      codeExample: {
        title: "Serialize and project",
        language: "typescript",
        code: "// Serialize (private info always stripped)\nconst json = majikId.toJSON();\nawait db.identities.upsert({ id: majikId.id, data: json });\n\n// Public-safe projection for API responses\nconst publicView = majikId.toPublicView();\nconsole.log('Display name:', publicView.display_name);\nconsole.log('Tier:', publicView.tier);\nconsole.log('Signing key fingerprint:', publicView.signing_key.fingerprint);\nconsole.log('Verification stages:', publicView.verification_stages);\n// {\n//   id_verification: true,\n//   liveness: false,\n//   face_match: false,\n//   phone_verification: true,\n//   ip_analysis: false\n// }\n\n// Validate identity state\nconst validation = majikId.validate();\nif (!validation.is_valid) {\n  console.error('Errors:', validation.errors);\n}",
      },
    },
    {
      id: "consent-management",
      title: "Consent Management",
      description:
        "Record and revoke data-sharing consents with a structured, auditable consent log. Consent operations require isMutable === true.",
      icon: "shield",
      content: [
        "grantConsent(grantedTo, scopes, expiresAt?) appends a ConsentEntry to the consent_log with a UUIDv7 consent_id, granted_to, scopes array, and is_active: true.",
        "revokeConsent(consentId) marks a specific entry as is_active: false and sets revoked_at.",
        "getActiveConsents() returns all entries where is_active === true.",
        "All consent operations require isMutable === true — they throw MajikUniversalIDImmutableError on verified identities.",
        "The consent_log is included in toJSON() output and is therefore persisted as part of the identity record.",
      ],
    },
  ],
  useCases: [
    {
      title: "Onboarding with Real-World KYC",
      description:
        "Create a MajikUniversalID for each user at onboarding, bind it to their MajikKey, and initiate a Didit session. As the user completes verification stages, webhook payloads graduate the IDTier automatically.",
      scenario:
        "A fintech platform requires verified users before allowing transactions. Each user creates a MajikKey, a MajikUniversalID is created server-side, and a Didit session is initiated with vendor_data set to the MajikID id. When Didit fires webhooks, processWebhook() updates the tier. The platform gates transactions at IDTier.TRUSTED.",
      benefits: [
        "Cryptographic link between user account and real-world identity",
        "Tier graduation is automatic — no manual webhook parsing",
        "Private personal data (from Didit Stage 1) is encrypted into the identity record at rest",
      ],
      codeExample: {
        title: "Onboarding flow",
        language: "typescript",
        code: "// 1. Create the identity\nconst majikId = await MajikUniversalID.create(user, key, {\n  account_id: 'acct_fintech',\n});\nawait db.identities.insert({ id: majikId.id, data: majikId.toJSON() });\n\n// 2. Create a Didit session (MajikDiditService handles this)\nconst session = await diditService.createSession({\n  vendor_data: majikId.id,  // CRITICAL — routes webhooks to this identity\n  workflow_id: 'wf_5stage',\n});\n\n// 3. Webhook handler\napp.post('/webhooks/didit', async (req, res) => {\n  const stored = await db.identities.findOne({ id: req.body.vendor_data });\n  const majikId = await MajikUniversalID.fromJSON(stored.data);\n\n  const result = await majikId.processWebhook(\n    req.body, req.headers, process.env.DIDIT_WEBHOOK_SECRET\n  );\n\n  await db.identities.update({ id: majikId.id, data: majikId.toJSON() });\n  res.json({ ok: true, new_tier: result.new_tier });\n});",
      },
      icon: "user",
    },
    {
      title: "Identity-Bound Content Signing",
      description:
        "Users sign documents, audio, video, or API payloads as their verified (or unverified) MajikUniversalID. Verifiers check the signature against the identity's public key bundle.",
      scenario:
        "A content platform lets creators sign their published works as their MajikUniversalID. MP3 files, PDFs, and video uploads each have the identity's hybrid signature embedded in their metadata. Consumers verify provenance without contacting the creator.",
      benefits: [
        "Proof of authorship anchored to a real-world verified identity",
        "Works for any file format supported by Majik Signature",
        "Verification is public-key only — no private key or server needed",
      ],
      codeExample: {
        title: "Sign and distribute a PDF",
        language: "typescript",
        code: "// Sign the PDF as this identity (requires isMutable)\nconst { blob: signedPdf } = await majikId.signFile(articlePdf, key, {\n  contentType: 'application/pdf',\n});\n\n// Publish signedPdf — signature is embedded in PDF metadata\nawait cdn.upload(`articles/${article.id}.pdf`, signedPdf);\n\n// Consumer verifies (using public view from an API)\nconst publicView = await api.getIdentityPublicView(signerId);\nconst verifierKeys = {\n  signerId: publicView.signing_key.fingerprint,\n  edPublicKey: base64ToBytes(publicView.signing_key.ed_public_key),\n  mlDsaPublicKey: base64ToBytes(publicView.signing_key.ml_dsa_public_key),\n};\nconst result = await MajikSignature.verifyFile(downloadedPdf, verifierKeys);\nconsole.log('Authentic:', result.valid);",
      },
      icon: "file",
    },
    {
      title: "Secure Private Data Custody",
      description:
        "Store encrypted personal information (legal name, government IDs, address) inside the identity record. Only the holder of the bound MajikKey can decrypt it — no server-side plaintext exposure.",
      scenario:
        "A platform stores user KYC data from Didit's Stage 1 extraction inside the MajikUniversalID as encrypted PrivatePersonalInfo. Only the user's MajikKey can decrypt. Support staff cannot access raw PII without the user's key.",
      benefits: [
        "PII is encrypted with the user's own ML-KEM-768 public key — zero-knowledge from the platform's perspective",
        "Decryption requires the user's unlocked MajikKey — passive database access yields no plaintext",
        "Group sharing via MajikEnvelope allows selective disclosure to specific recipients",
      ],
      icon: "lock",
    },
    {
      title: "Cross-Device Identity Recovery",
      description:
        "A MajikUniversalID serializes to JSON and is fully portable. Loaded on any device with the corresponding MajikKey, it provides the same identity — including access to decrypted private info after unlock.",
      scenario:
        "A user sets up the Majikah app on a new phone. They import their MajikKey via mnemonic backup, load their stored MajikUniversalID JSON from the server, and call fromJSON(json, { key }). Their identity — including verification tier and encrypted private info — is immediately available.",
      benefits: [
        "Identity is self-contained and portable — no re-verification needed on a new device",
        "fromJSON() with { key } silently decrypts private info on load",
        "Integrity hash verification on load catches any server-side tampering",
      ],
      icon: "refresh",
    },
  ],
  examples: [
    {
      title: "Identity Lifecycle",
      description: "Create, serialize, load, and validate a MajikUniversalID.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Create and serialize",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikUniversalID } from '@majikah/majik-universal-id';\n\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\n\nconst majikId = await MajikUniversalID.create(user, key, {\n  account_id: 'acct_demo',\n});\n\nconsole.log('ID:', majikId.id);\nconsole.log('Tier:', majikId.tier);         // IDTier.UNVERIFIED\nconsole.log('Mutable:', majikId.isMutable); // true\nconsole.log('Private decrypted:', majikId.isPrivateDecrypted); // true\n\n// Serialize — private info is always stripped\nconst json = majikId.toJSON();\nawait db.save(majikId.id, json);",
        },
        {
          title: "Load and decrypt private info",
          language: "typescript",
          code: "const key = MajikKey.fromJSON(storedKey);\nawait key.unlock('passphrase');\n\n// Load with silent decryption\nconst majikId = await MajikUniversalID.fromJSON(storedJson, { key });\n\nif (majikId.isPrivateDecrypted) {\n  const info = majikId.privateInfo;\n  console.log('Legal name:', info.legal_first_name, info.legal_last_name);\n  console.log('DOB:', info.date_of_birth);\n  console.log('Phone:', info.primary_phone); // E.164\n}\n\n// Lock key after use\nkey.lock();",
        },
        {
          title: "Validate identity state",
          language: "typescript",
          code: "const validation = majikId.validate();\nconsole.log('Is valid:', validation.is_valid);\nconsole.log('Errors:', validation.errors);\nconsole.log('Warnings:', validation.warnings);\n// Warning example:\n// 'private info is encrypted and not yet decrypted in this session'\n// 're_verification_required is set'",
        },
      ],
    },
    {
      title: "Verification Stage Checks",
      description:
        "Query the identity's verification tier, stages, and summary.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Check tier and stages",
          language: "typescript",
          code: "import { DiditStage, IDTier } from '@majikah/majik-universal-id';\n\nconsole.log('Tier:', majikId.tier);\nconsole.log('Is verified:', majikId.isVerified);\nconsole.log('Is trusted:', majikId.isTrusted);\nconsole.log('Verified at:', majikId.verifiedAt);\nconsole.log('Lock days remaining:', majikId.verificationLockDaysRemaining);\n\n// Check individual stages\nconst phonePassed = majikId.isVerificationPassed(DiditStage.PHONE_VERIFICATION);\nconst livenessPassed = majikId.isVerificationPassed(DiditStage.LIVENESS);\n\n// All passed stages in order\nconst passedStages = majikId.getPassedVerifications();\nconsole.log('Passed:', passedStages);\n\n// Full summary\nconst summary = majikId.verificationSummary;\nconsole.log('AML clear:', summary.aml_clear);\nconsole.log('Biometric status:', summary.biometric_status);\nconsole.log('IP risk level:', summary.ip_risk_level);",
        },
        {
          title: "Public view projection",
          language: "typescript",
          code: "// Safe for public API responses — no private info\nconst publicView = majikId.toPublicView();\n\nconsole.log('Display name:', publicView.display_name);\nconsole.log('Tier:', publicView.tier);\nconsole.log('Signing key fingerprint:', publicView.signing_key.fingerprint);\n\n// All 5 stage keys are always present\nconst stages = publicView.verification_stages;\nconsole.log('ID verified:', stages[DiditStage.ID_VERIFICATION]);   // true/false\nconsole.log('Liveness:', stages[DiditStage.LIVENESS]);              // true/false\nconsole.log('Phone:', stages[DiditStage.PHONE_VERIFICATION]);       // true/false",
        },
      ],
    },
    {
      title: "Content Signing & Verification",
      description: "Sign content and files, then verify against the identity.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Sign and verify a string",
          language: "typescript",
          code: "// Identity must be UNVERIFIED (isMutable)\nconst signature = await majikId.signContent(\n  'My signed statement.',\n  key,\n  { contentType: 'text/plain' }\n);\n\nconst result = majikId.verifyContent('My signed statement.', signature);\nconsole.log('Valid:', result.valid);               // true\nconsole.log('Registered:', result.signer_registered); // true\n\n// Tamper detection\nconst bad = majikId.verifyContent('Tampered.', signature);\nconsole.log('Tampered valid:', bad.valid);         // false",
        },
        {
          title: "Sign and verify a file",
          language: "typescript",
          code: "// Sign and embed into a file\nconst { blob: signedMp3, handler } = await majikId.signFile(mp3Blob, key);\nconsole.log('Handler:', handler); // \"MP3\"\n\n// Verify later\nconst result = await majikId.verifyFile(signedMp3);\nif (result.valid) {\n  console.log('Authentic. Signed by:', result.signer_fingerprint);\n  console.log('Signed at:', result.signed_at);\n  console.log('Handler:', result.handler); // \"MP3\"\n}",
        },
      ],
    },
    {
      title: "Webhook Processing",
      description: "Process Didit webhooks and apply user sync actions.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Webhook handler (Express)",
          language: "typescript",
          code: "import express from 'express';\nimport { MajikUniversalID } from '@majikah/majik-universal-id';\n\napp.post('/webhooks/didit', express.raw({ type: 'application/json' }), async (req, res) => {\n  const payload = JSON.parse(req.body.toString());\n  const stored = await db.identities.findOne({ id: payload.vendor_data });\n\n  if (!stored) return res.status(404).json({ error: 'Identity not found' });\n\n  const majikId = await MajikUniversalID.fromJSON(stored.data);\n\n  const result = await majikId.processWebhook(\n    payload,\n    req.headers as any,\n    process.env.DIDIT_WEBHOOK_SECRET!\n  );\n\n  // Apply user sync actions\n  if (result.user_sync_actions.includes('verifyIdentity')) {\n    await userService.setIdentityVerified(majikId.userId);\n  }\n  if (result.user_sync_actions.includes('verifyPhone')) {\n    await userService.setPhoneVerified(majikId.userId);\n  }\n\n  // Persist updated identity\n  await db.identities.update({ id: majikId.id, data: majikId.toJSON() });\n\n  res.json({\n    ok: true,\n    previous_tier: result.previous_tier,\n    new_tier: result.new_tier,\n    tier_changed: result.tier_changed,\n  });\n});",
        },
        {
          title: "Webhook handler (Cloudflare Worker)",
          language: "typescript",
          code: "export default {\n  async fetch(request: Request, env: Env): Promise<Response> {\n    if (request.method !== 'POST') return new Response('Not found', { status: 404 });\n\n    const rawBody = await request.text();\n    const payload = JSON.parse(rawBody);\n    const headers = Object.fromEntries(request.headers.entries());\n\n    const stored = await env.KV.get(`identity:${payload.vendor_data}`, 'json');\n    if (!stored) return new Response('Not found', { status: 404 });\n\n    const majikId = await MajikUniversalID.fromJSON(stored);\n    const result = await majikId.processWebhook(payload, headers, env.DIDIT_SECRET);\n\n    await env.KV.put(`identity:${majikId.id}`, JSON.stringify(majikId.toJSON()));\n\n    return Response.json({ ok: true, new_tier: result.new_tier });\n  },\n};",
        },
      ],
    },
    {
      title: "Advanced: Revocation & Consent",
      description: "Revoke verification and manage consent records.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Revoke verification",
          language: "typescript",
          code: "import {\n  MajikUniversalID,\n  MajikUniversalIDVerificationLockedError,\n} from '@majikah/majik-universal-id';\n\ntry {\n  majikId.revokeVerification('Admin requested reset — fraudulent documents detected.');\n  console.log('Revoked. New tier:', majikId.tier); // IDTier.UNVERIFIED\n  console.log('Mutable again:', majikId.isMutable); // true\n} catch (e) {\n  if (e instanceof MajikUniversalIDVerificationLockedError) {\n    console.warn(\n      `Cannot revoke — locked for ${e.days_remaining} more day(s) since ${e.verified_at}`\n    );\n  }\n}",
        },
        {
          title: "Consent management",
          language: "typescript",
          code: "// Grant consent (requires isMutable)\nmajikId.grantConsent(\n  'partner-analytics-service',\n  ['read:profile', 'read:verification_status'],\n  new Date(Date.now() + 90 * 86400000).toISOString() // 90 days\n);\n\n// View active consents\nconst active = majikId.getActiveConsents();\nconsole.log('Active consents:', active.map(c => c.granted_to));\n\n// Revoke a specific consent\nmajikId.revokeConsent(active[0].consent_id);\nconsole.log('Remaining active:', majikId.getActiveConsents().length);",
        },
      ],
    },
    {
      title: "Advanced: Error Handling",
      description: "Handle the typed error hierarchy with type guards.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Typed error handling",
          language: "typescript",
          code: "import {\n  isUniversalIDError,\n  isImmutableError,\n  isLockedError,\n  isPrivateInfoLockedError,\n  isWebhookError,\n  MajikUniversalIDVerificationLockedError,\n  MajikUniversalIDImmutableError,\n} from '@majikah/majik-universal-id';\n\ntry {\n  await majikId.signContent('test', key);\n} catch (e) {\n  if (isImmutableError(e)) {\n    console.error('Identity is verified, tier:', e.current_tier);\n  } else if (isUniversalIDError(e)) {\n    console.error('SDK error:', e.code, e.message);\n  } else {\n    throw e; // re-throw unexpected errors\n  }\n}\n\n// Check for private info locked\ntry {\n  const info = majikId.privateInfo;\n} catch (e) {\n  if (isPrivateInfoLockedError(e)) {\n    const result = await majikId.decryptPrivate(key);\n    if (!result.success) console.warn(result.reason);\n  }\n}",
        },
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Primitives",
        table: {
          headers: ["Primitive", "Source Package", "Usage"],
          rows: [
            {
              cells: [
                "Ed25519",
                "@majikah/majik-signature (via @stablelib/ed25519)",
                "Identity signing (classical, 64-byte sig)",
              ],
            },
            {
              cells: [
                "ML-DSA-87 (FIPS-204)",
                "@majikah/majik-signature (via @noble/post-quantum)",
                "Identity signing (post-quantum, 4595-byte sig)",
              ],
            },
            {
              cells: [
                "ML-KEM-768 (FIPS-203)",
                "@majikah/majik-envelope (via @noble/post-quantum)",
                "Private personal info encryption",
              ],
            },
            {
              cells: [
                "SHA3-512",
                "@noble/hashes",
                "Integrity hash over id, user_id, timestamp, and all four public keys",
              ],
            },
            {
              cells: [
                "SHA-256",
                "@majikah/majik-signature",
                "Content hashing within signature envelopes",
              ],
            },
            {
              cells: [
                "AES-256-GCM",
                "@majikah/majik-envelope",
                "Symmetric encryption of private info envelope",
              ],
            },
          ],
        },
      },
      {
        title: "Identity Tiers",
        table: {
          headers: ["Tier", "Enum Value", "Required Stages", "isMutable"],
          rows: [
            {
              cells: ["Unverified", "IDTier.UNVERIFIED", "None", "true"],
            },
            {
              cells: ["Basic", "IDTier.BASIC", "Stage 4 (Phone) only", "false"],
            },
            {
              cells: ["Verified", "IDTier.VERIFIED", "Stage 1 (ID)", "false"],
            },
            {
              cells: [
                "Enhanced",
                "IDTier.ENHANCED",
                "Stages 1 + 2 + 3",
                "false",
              ],
            },
            {
              cells: ["Trusted", "IDTier.TRUSTED", "All 5 stages", "false"],
            },
          ],
        },
      },
      {
        title: "Integrity Hash Canonical Format",
        description:
          "The SHA3-512 hash is computed over a colon-delimited string of identity fields and public keys:",
        list: [
          "Format: id : user_id : timestamp : x25519_pk : ed_pk : ml_kem_pk : ml_dsa_pk",
          "id — UUIDv7 identity record ID",
          "user_id — MajikUser ID",
          "timestamp — ISO 8601 creation timestamp",
          "x25519_pk — X25519 public key (base64)",
          "ed_pk — Ed25519 public key (base64)",
          "ml_kem_pk — ML-KEM-768 public key (base64, 1184 bytes)",
          "ml_dsa_pk — ML-DSA-87 public key (base64, 2592 bytes)",
          "All four public keys are committed — substituting any one produces a different hash",
        ],
      },
      {
        title: "Webhook HMAC Verification Strategy",
        description:
          "verifyWebhookSignatureRaw() attempts signature verification in this order:",
        list: [
          "1. x-signature-v2 against raw body string (preferred — avoids re-serialization risk)",
          "2. x-signature-v2 against canonical sorted+rounded JSON (Didit V2 canonical form)",
          "3. x-signature-simple against session fields (timestamp:session_id:status:webhook_type)",
          "Timestamp freshness is enforced — Math.abs(now - timestamp) > 300 seconds throws MajikUniversalIDWebhookSignatureError",
          "Comparison uses constant-time string equality to prevent timing attacks",
        ],
      },
      {
        title: "Key Binding Constraints",
        list: [
          "1:1:1 binding — one MajikUniversalID binds exactly one MajikUser and one MajikKey",
          "The bound key's fingerprint is stored in signing_key.fingerprint — all signing and verification checks this",
          "To use a different key, create a new MajikUniversalID — there is no key rotation mechanism",
          "All four public keys (X25519, Ed25519, ML-KEM-768, ML-DSA-87) must be present on the MajikKey at create() time",
          "key.isFullyUpgraded must be true — keys without ML-KEM are rejected at create()",
        ],
      },
    ],
  },
  compatibility: {
    title: "Compatibility",
    description:
      "@majikah/majik-universal-id is distributed as an ES module. It requires environments that support SubtleCrypto, Uint8Array, and crypto.getRandomValues. Works isomorphically in Node.js and modern browsers.",
    runtime: [
      {
        name: "Node.js",
        supported: true,
        minVersion: "18",
        notes:
          "SubtleCrypto available via globalThis.crypto since Node 18. Full support for all features.",
      },
      {
        name: "Browser",
        supported: true,
        notes:
          "All modern browsers supported. SubtleCrypto requires HTTPS. File signing requires Blob/File API support.",
      },
      {
        name: "Electron",
        supported: true,
        notes:
          "Works in both main and renderer process. File signing works with Electron's file dialogs.",
      },
      {
        name: "Cloudflare Workers",
        supported: true,
        notes:
          "SubtleCrypto and crypto.getRandomValues available natively. No fs module — file signing works with Blob/ArrayBuffer.",
      },
      {
        name: "Bun",
        supported: true,
        notes:
          "Full compatibility. Bun supports SubtleCrypto and file I/O natively.",
      },
      {
        name: "Deno",
        supported: true,
        notes: "Requires --allow-net and --allow-read for file operations.",
      },
    ],
    typescript: {
      included: true,
      strictMode: true,
      declaration: true,
    },
  },
  bestPractices: {
    title: "Best Practices",
    description:
      "Recommendations for integrating Majik Universal ID securely into your application.",
    practices: [
      {
        title:
          "Set vendor_data to MajikUniversalID.id when creating Didit sessions",
        description:
          "The webhook routing mechanism depends on vendor_data matching the MajikID id. If vendor_data is absent or mismatched, processWebhook() throws MajikUniversalIDWebhookPayloadError.",
        dos: [
          "Always set vendor_data: majikId.id when calling the Didit session creation API",
          "Store the MajikID id alongside the Didit session_id in your database for lookup",
        ],
        donts: [
          "Don't use the user_id or any other field as vendor_data — use the MajikID id specifically",
          "Don't call processWebhook() on a payload whose vendor_data doesn't match your stored identity",
        ],
        category: "Security",
      },
      {
        title: "Lock keys immediately after signing",
        description:
          "Private key material should be in memory only for the duration of the signing operation. Lock as soon as signing is complete.",
        dos: [
          "Call key.lock() immediately after signContent(), signFile(), or any MajikKey operation",
          "Keep the unlock window as short as possible — ideally within a single function scope",
          "Check key.isFullyUpgraded before calling create() — keys without ML-KEM will be rejected",
        ],
        donts: [
          "Don't hold a MajikKey in the unlocked state indefinitely",
          "Don't pass an unlocked key across asynchronous boundaries unnecessarily",
        ],
        category: "Security",
      },
      {
        title:
          "Always verify signer_fingerprint after verifyContent() or verifyFile()",
        description:
          "verifyContent() returns signer_registered: true only if the fingerprint in the signature matches the bound key. Always check this field alongside valid.",
        dos: [
          "Check result.valid && result.signer_registered before trusting a verification result",
          "Cross-reference result.signer_fingerprint against majikId.signingKey.fingerprint for belt-and-suspenders verification",
          "Use toPublicView() for public-facing verifier data — it exposes the fingerprint and public key material safely",
        ],
        donts: [
          "Don't trust result.valid alone without checking signer_registered",
          "Don't use the public keys from extractPublicKeys() without first verifying the signer fingerprint against a trusted source",
        ],
        category: "Security",
      },
      {
        title: "Use toPublicView() for public API responses",
        description:
          "Never serialize the full toJSON() output in a public API response. toPublicView() is specifically designed for safe public consumption.",
        dos: [
          "Return toPublicView() from any endpoint that exposes identity data publicly",
          "Use verification_stages (all 5 keys always present) for UI-facing tier display",
        ],
        donts: [
          "Don't return toJSON() from public endpoints — it contains the encrypted private envelope",
          "Don't expose metadata.private in any form outside of the identity owner's authenticated session",
        ],
        category: "Security",
      },
      {
        title: "Persist immediately after processWebhook()",
        description:
          "processWebhook() mutates the internal state of the MajikUniversalID instance. Persist toJSON() immediately after each webhook call to avoid losing tier graduation state.",
        dos: [
          "Call await db.update(majikId.toJSON()) as the last step in every webhook handler",
          "Handle webhook idempotency — Didit may deliver the same event more than once",
        ],
        donts: [
          "Don't delay persistence — if the process crashes after processWebhook() but before save, the tier graduation is lost",
        ],
        category: "Maintainability",
      },
      {
        title: "validate() after loading from untrusted storage",
        description:
          "fromJSON() verifies the SHA3-512 hash automatically and will throw MajikUniversalIDIntegrityError on mismatch. Calling validate() additionally surfaces structural warnings.",
        dos: [
          "Call validate() after fromJSON() when loading from sources you don't fully control",
          "Check validation.warnings for runtime signals like 'private info not yet decrypted' or 're-verification required'",
        ],
        donts: [
          "Don't skip fromJSON()'s integrity check by constructing instances manually",
        ],
        category: "Security",
      },
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    commonIssues: [
      {
        problem:
          "create() throws 'MajikKey is missing required public key fields (ed, mlDsa, or mlKem)'",
        symptoms: [
          "MajikUniversalIDKeyError thrown from create()",
          "key.isFullyUpgraded is false",
          "key.hasMlKem is false",
        ],
        causes: [
          "The key was created with an older version of @majikah/majik-key that did not include ML-KEM-768",
          "The key was loaded from a legacy JSON snapshot via fromJSON() without migration",
        ],
        solutions: [
          "Re-import the key via MajikKey.importFromMnemonicBackup() to add all key fields",
          "Check key.isFullyUpgraded before calling create() — if false, run the migration first",
          "Verify key.hasSigningKeys before calling create() for an explicit check on Ed25519 and ML-DSA fields",
        ],
      },
      {
        problem:
          "processWebhook() throws MajikUniversalIDWebhookPayloadError: 'vendor_data does not match'",
        symptoms: [
          "MajikUniversalIDWebhookPayloadError thrown from processWebhook()",
          "payload.vendor_data !== majikId.id",
        ],
        causes: [
          "The Didit session was created with a different vendor_data value than the MajikUniversalID.id",
          "The wrong MajikUniversalID was loaded for the incoming webhook",
          "vendor_data was set to user_id or another identifier instead of the MajikID id",
        ],
        solutions: [
          "Ensure vendor_data: majikId.id is always passed when creating a Didit session",
          "Look up the identity by vendor_data from the webhook payload — not by user_id",
          "Log payload.vendor_data at the start of the webhook handler for debugging",
        ],
      },
      {
        problem: "signContent() throws MajikUniversalIDImmutableError",
        symptoms: [
          "MajikUniversalIDImmutableError thrown from signContent(), signFile(), or other mutating methods",
          "majikId.isMutable is false",
          "majikId.tier is not IDTier.UNVERIFIED",
        ],
        causes: [
          "The identity was graduated to a verified tier by processWebhook() and is now immutable",
          "A mutating method was called after the identity was verified",
        ],
        solutions: [
          "Check majikId.isMutable before calling any mutating method",
          "If mutation is required on a verified identity, call revokeVerification() — but only after the 30-day lock expires",
          "Check majikId.isVerificationLocked and majikId.verificationLockDaysRemaining before attempting revocation",
        ],
      },
      {
        problem:
          "fromJSON() throws MajikUniversalIDIntegrityError: 'hash mismatch'",
        symptoms: [
          "MajikUniversalIDIntegrityError thrown from fromJSON() or fromBase64()",
          "The stored hash does not match the recomputed hash",
        ],
        causes: [
          "The stored JSON was modified after creation — either intentionally (tampering) or accidentally (partial write, encoding issue)",
          "The signing_key fields were modified directly in the stored JSON",
          "A serialization round-trip changed the base64 encoding of a public key field",
        ],
        solutions: [
          "Treat the serialized JSON as an opaque blob — do not modify individual fields after creation",
          "Check for encoding issues — ensure base64 strings are not double-encoded or trimmed",
          "If the record is recoverable from source data, recreate via MajikUniversalID.create() and re-run processWebhook() for the verification state",
        ],
      },
      {
        problem:
          "decryptPrivate() returns { success: false, reason: 'Key fingerprint does not match' }",
        symptoms: [
          "decryptPrivate() returns success: false",
          "reason contains 'fingerprint'",
        ],
        causes: [
          "The key provided to decryptPrivate() is a different key than the one bound to this identity",
          "The identity was created with one key but a different key is being used for decryption",
        ],
        solutions: [
          "Verify key.fingerprint === majikId.signingKey.fingerprint before calling decryptPrivate()",
          "Load the correct MajikKey JSON for this identity — the fingerprint in signing_key.fingerprint is the canonical identifier",
        ],
      },
      {
        problem:
          "verifyFile() returns valid: false on a file that was just signed",
        symptoms: [
          "signFile() succeeded and returned a blob",
          "verifyFile() on the same blob returns valid: false",
        ],
        causes: [
          "The file was re-encoded, transcoded, or modified after signing",
          "The blob was serialized and deserialized in a way that changed its bytes (e.g., base64 → buffer mismatch)",
          "A Tier 2 (universal trailer) signature was used on a format that strips trailing bytes on re-save",
        ],
        solutions: [
          "Ensure the blob passed to verifyFile() is the exact bytes returned by signFile()",
          "Check the handler from signFile() — if it was 'Fallback (Universal Trailer)', the signature may not survive re-save",
          "Use isSigned() from Majik Signature as a quick diagnostic before verifyFile()",
        ],
      },
    ],
    debugging: [
      {
        title: "Inspect identity state without private info",
        description:
          "Use the metadata getter and getters to examine the identity state without needing private info decryption.",
        code: {
          language: "typescript",
          code: "// These getters are always available — no decryption needed\nconsole.log('ID:', majikId.id);\nconsole.log('Tier:', majikId.tier);\nconsole.log('Status:', majikId.status);\nconsole.log('Is mutable:', majikId.isMutable);\nconsole.log('Is verified:', majikId.isVerified);\nconsole.log('Is trusted:', majikId.isTrusted);\nconsole.log('Verified at:', majikId.verifiedAt);\nconsole.log('Is locked:', majikId.isVerificationLocked);\nconsole.log('Lock days remaining:', majikId.verificationLockDaysRemaining);\nconsole.log('Signing key fingerprint:', majikId.signingKey.fingerprint);\nconsole.log('Private decrypted:', majikId.isPrivateDecrypted);",
        },
      },
      {
        title: "Check identity validity and warnings",
        description:
          "validate() surfaces structural errors and runtime warnings without throwing.",
        code: {
          language: "typescript",
          code: "const v = majikId.validate();\nif (!v.is_valid) {\n  console.error('Errors:', v.errors);\n  // e.g. 'integrity hash mismatch'\n  // e.g. 'private info is missing encryption envelope'\n}\nconsole.log('Warnings:', v.warnings);\n// e.g. 'private info is encrypted and not yet decrypted in this session'\n// e.g. 'verification lock active — N day(s) remaining'",
        },
      },
      {
        title: "Inspect the verification summary",
        description:
          "verificationSummary gives a full snapshot of KYC state for debugging or admin UIs.",
        code: {
          language: "typescript",
          code: "const s = majikId.verificationSummary;\nconsole.log('Tier:', s.tier);\nconsole.log('Status:', s.status);\nconsole.log('All stages passed:', s.all_stages_passed);\nconsole.log('Completed stages:', s.completed_stages);\nconsole.log('Biometric status:', s.biometric_status);\nconsole.log('AML clear:', s.aml_clear);\nconsole.log('IP risk level:', s.ip_risk_level);\nconsole.log('Email verified:', s.user_verification.email_verified);\nconsole.log('Phone verified:', s.user_verification.phone_verified);\nconsole.log('Identity verified:', s.user_verification.identity_verified);",
        },
      },
    ],
  },
  ecosystem: {
    title: "Ecosystem",
    paragraphs: [
      "@majikah/majik-universal-id is the identity layer of the Majikah SDK stack. It sits above @majikah/majik-key (key management), @majikah/majik-signature (content signing), and @majikah/majik-envelope (encryption), combining all three into a single cohesive identity record.",
      "Identities can be created and managed through the official web application at id.majikah.solutions — users can create their own MajikUniversalID using their own Majik Keys directly in the browser.",
      "The KYC verification workflow is powered by Didit. Didit API calls (session creation, token refresh) are handled by a separate MajikDiditService package, keeping this library a pure domain model with no HTTP dependencies.",
    ],
    relatedPackages: [
      {
        name: "@majikah/majik-key",
        description:
          "Post-quantum seed phrase identity library. Required peer dependency — provides the MajikKey accounts that are bound to every MajikUniversalID.",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        relationship: "peer",
      },
      {
        name: "@majikah/majik-signature",
        description:
          "Hybrid post-quantum content signing library. Required peer dependency — powers signContent(), signFile(), verifyContent(), and verifyFile().",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
        relationship: "peer",
      },
      {
        name: "@majikah/majik-envelope",
        description:
          "Post-quantum group encryption library. Required peer dependency — used to encrypt and share PrivatePersonalInfo via ML-KEM-768.",
        url: "https://www.npmjs.com/package/@majikah/majik-envelope",
        relationship: "peer",
      },
      {
        name: "@thezelijah/majik-user",
        description:
          "User profile model for the Majikah ecosystem. Required peer dependency — MajikUser is the user object bound to every MajikUniversalID at creation.",
        url: "https://www.npmjs.com/package/@thezelijah/majik-user",
        relationship: "peer",
      },
      {
        name: "@noble/hashes",
        description:
          "Audited pure-JS cryptographic hash functions. Provides SHA3-512 for integrity hashing.",
        url: "https://www.npmjs.com/package/@noble/hashes",
        relationship: "dependency",
      },
      {
        name: "Majik ID Web App",
        description:
          "Official web application for creating and managing Majik Universal IDs at id.majikah.solutions. Users can create their own IDs using their own Majik Keys directly in the browser.",
        url: "https://id.majikah.solutions",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-message",
        description:
          "Post-quantum E2EE messaging SDK. Uses MajikUniversalID for identity-bound communication in the Majikah messaging platform.",
        url: "https://www.npmjs.com/package/@majikah/majik-message",
        relationship: "complement",
      },
    ],
  },
  support: {
    title: "Support & Resources",
    documentation: [
      {
        label: "GitHub Repository",
        url: "https://github.com/Majikah/majik-universal-id",
        type: "reference",
      },
      {
        label: "npm Package",
        url: "https://www.npmjs.com/package/@majikah/majik-universal-id",
        type: "reference",
      },
      {
        label: "ID Web App",
        url: "https://id.majikah.solutions",
        type: "guide",
      },
    ],
    community: [
      {
        platform: "GitHub Issues",
        url: "https://github.com/Majikah/majik-universal-id/issues",
      },
      {
        platform: "GitHub Sponsors",
        url: "https://github.com/sponsors/jedlsf",
      },
    ],
    issues: {
      url: "https://github.com/Majikah/majik-universal-id/issues",
    },
  },
  cta: {
    title: "Start Building with Majik Universal ID",
    description:
      "Add post-quantum anchored identity and KYC verification to your app. Create an identity, process Didit webhooks to graduate tiers, sign content and files, and manage encrypted private info — all from one class.",
    actions: [
      {
        label: "npm install @majikah/majik-universal-id",
        url: "https://www.npmjs.com/package/@majikah/majik-universal-id",
        type: "install",
      },
      {
        label: "Create Your ID at id.majikah.solutions",
        url: "https://id.majikah.solutions",
        type: "demo",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-universal-id",
        type: "github",
      },
    ],
    closingStatement:
      "Built by Zelijah for the Majikah ecosystem. Open source under Apache-2.0.",
  },
};
